import React, { useState, useEffect } from 'react'
import { Content } from 'organisms/Content'
import CreateRoleHeader from './CreateRoleHeader'
import CreateRoleSettings from './CreateRoleSettings'
import { Tabs } from 'atoms/Tabs'
import TreeViewCatalog from 'organisms/TreeViewCatalog'
import { connect } from 'react-redux'
import translate from 'core/translate'
import moment from 'moment'
import omitEmpty from 'omit-empty'
import deepEqual from 'fast-deep-equal'
import { routes } from 'features/routes'

import styles from './styles'
import helpers from 'core/helpers'
import { api } from 'core/api/ConnectionManager'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import { isNotEmptyArray } from 'core/utils/coreUtil'
import { changeLocation } from 'features/routes'
import { emitError } from 'features/appNotifications/AppNotifications.state'
import { checkAvailableModule } from '../../../../core/helpers/routes/routes'
import { IRoleActionRule } from '../../../../core/models/Roles'
import { cn } from 'ethcss'
import { item } from 'theme'
import Catalog from '../../../../blocks.app/catalog/catalog'
import { ModerationRoleSettings } from './moderation-role-settings'
import { Action, MakeAction, Permission, PermissionAction, PermissionActionKey } from './types'

interface SpecificRules {
    [index: string]: string[]
}

interface CatalogTypes {
    [index: string]: string
}

type Payload = {
    [actionKey in PermissionActionKey]?: PermissionAction
}

type PayloadAction = {
    permissionName: string
    actionId: number
    active: boolean
}

interface PermissionRulePayload {
    name: string
    options: unknown
}

interface PermissionActionPayload {
    actionId: number
    rules?: PermissionRulePayload[]
}

interface PermissionPayload {
    resourceId: number
    actions: PermissionActionPayload[]
}

const AVAILABLE_PERMISSION_SECTIONS = [
    'dashboard',
    'digitalSignage',
    'scoreboard',
    'advertisingCampaign',
    'advertisingAds',
    'broadcast',
    'instancesCatalog',
    'schedule',
    'timetable',
    'files',
    'role',
    'user',
    'statistics',
    'settings',
    'DSNotifications',
    'widgetsGallery',
    'addContentToDevice',
    'videoChat',
    'notificationPage',
    'notificationCenter',
    'userHistory',
    'emailHistory',
    'moderation',
]

const CATALOG_TYPES: CatalogTypes = {
    digitalSignage: 'digitalSignages',
    files: 'files',
    broadcast: 'broadcasts',
}

const NOTIFICATION_PAGE_TABS = ['notificationCenter', 'userHistory', 'emailHistory']

const AVAILABLE_CATALOGS = ['digitalSignage', 'broadcast', 'files']
const AVAILABLE_OLD_CATALOGS = ['advertisingAds']
const SPECIFIC_ACTIONS_RULES: SpecificRules = {
    digitalSignage: ['update'],
    files: ['read'],
}

const SPECIFIC_ACTIONS_RULES_OPTIONS_KEYS = ['type', 'data.type', 'subactions']

const makeActionObject = (data: Permission[]) => {
    const permissions = data.filter((permission: Permission) => AVAILABLE_PERMISSION_SECTIONS.includes(permission.name))
    const actions: MakeAction = {}

    permissions.forEach((permission: Permission) =>
        permission.actions.forEach((action: Action) => {
            actions[permission.id] = {
                ...actions[permission.id],
                [action.name]: {
                    permissionName: permission.name,
                    actionId: action.id,
                    active: false,
                },
            }
        })
    )

    return actions
}

const makeActionsWithUserPermissions = (permissions: Permission[], allPermissions: Permission[]) => {
    const allActions: any = makeActionObject(allPermissions)
    const copiedPermissions = helpers.deepCopy(permissions)
    copiedPermissions.forEach((permission: Permission) => {
        permission.actions.forEach((action: Action) => {
            if (allActions[permission.id] && allActions[permission.id][action.name]) {
                const actionData: any = {
                    [action.name]: {
                        permissionName: permission.name,
                        actionId: action.id,
                        active: true,
                    },
                }

                if (action.rules && action.rules.length) {
                    action.rules.forEach((rule: IRoleActionRule) => {
                        if (rule.name === 'filterByProperty') {
                            Object.entries(rule.options).forEach(([key, value]) => {
                                if (SPECIFIC_ACTIONS_RULES_OPTIONS_KEYS.includes(key)) {
                                    if (!actionData[action.name].additionalRules) {
                                        actionData[action.name].additionalRules = {
                                            filterByProperty: {},
                                        }
                                    }

                                    actionData[action.name].additionalRules.filterByProperty[key] = value
                                } else {
                                    if (!actionData[action.name].filterByProperty) {
                                        actionData[action.name].filterByProperty = {}
                                    }
                                    actionData[action.name].filterByProperty[key] = value
                                }
                            })
                        }
                    })
                }

                allActions[permission.id] = {
                    ...allActions[permission.id],
                    ...actionData,
                }
            }
        })
    })

    return allActions
}

const generateRoleLabel = () => `${translate('newRole')} ${moment().format('HH:mm:ss DD/MM/YYYY')}`

const actionsWithoutRules = ['create']

const CreateRole = (p_: any) => {
    const [actions, setActions] = useState(() => makeActionObject(p_.permissions))
    const [initialActions, setInitialActions] = useState({})
    const [label, setLabel] = useState(generateRoleLabel())
    const [editedRole, setEditedRole] = useState<any>(null)
    const [prevPermissions, setPrevPrermissions] = useState(null)
    const query = getURLSearchParamsByLocation(p_.location)
    let roleId = query.roleId

    useEffect(() => {
        if (deepEqual(prevPermissions, p_.permissions)) {
            return
        }

        if (roleId) {
            api.send('getRoleById', { roleId }).then((role: any) => {
                setEditedRole(role)
                setLabel(role.label)
                setActions(makeActionsWithUserPermissions(role.permissions, p_.permissions))
                setInitialActions(makeActionsWithUserPermissions(role.permissions, p_.permissions))
            })
        } else {
            setLabel(generateRoleLabel())
            setActions(makeActionObject(p_.permissions))
            setInitialActions(makeActionObject(p_.permissions))
            setEditedRole(null)
        }

        setPrevPrermissions(p_.permissions)
    }, [roleId, p_.permissions, prevPermissions])

    const isAllowActionWithoutRules = (actionKey: string) => {
        return actionsWithoutRules.includes(actionKey)
    }

    const isActionMustContainRules = (permissionName: string, actionKey: string) => {
        const requiredRules = SPECIFIC_ACTIONS_RULES[permissionName]

        return requiredRules && requiredRules.includes(actionKey)
    }

    const isCatalogTreeView = (permissionName: string) => {
        return CATALOG_TYPES[permissionName]
    }

    const getPermissionsArray = () => {
        const omited = omitEmpty(actions)
        const permissions: PermissionPayload[] = []

        Object.entries(omited).forEach(([resourceId, permissionActions]) => {
            const model: PermissionPayload = {
                resourceId: parseInt(resourceId, 10),
                actions: [],
            }

            Object.entries(permissionActions).forEach(([actionKey, action]) => {
                if (!action?.active) return

                const rules: PermissionRulePayload[] = []

                if (action.filterByProperty) {
                    rules.push({
                        name: 'filterByProperty',
                        options: action.filterByProperty,
                    })
                }

                if (action.additionalRules) {
                    Object.entries(action.additionalRules).forEach(([ruleKey, ruleOptions]) => {
                        rules.push({
                            name: ruleKey,
                            options: ruleOptions,
                        })
                    })
                }

                if (isActionMustContainRules(action.permissionName, actionKey) && !isNotEmptyArray(rules)) return

                const actionModel: PermissionActionPayload = {
                    actionId: action.actionId,
                }

                if (isAllowActionWithoutRules(actionKey) || isNotEmptyArray(rules)) {
                    actionModel.rules = rules
                }

                if (isCatalogTreeView(action.permissionName) && !actionModel.rules) return

                model.actions.push(actionModel)
            })

            if (model.actions.length) {
                permissions.push(model)
            }
        })

        return permissions
    }

    const handleChangeActions = (payload: MakeAction) => {
        setActions((prev) => ({
            ...prev,
            ...payload,
        }))
    }

    const handleChangeLabel = (label: string) => setLabel(label)

    const handleModerationPermissionsChange = (isChecked: boolean, moderationPermission: Permission) => {
        if (!moderationPermission) return

        const moderationId = moderationPermission.id
        const updateAction = moderationPermission.actions.find((a) => a.name === 'update')

        if (!updateAction) return

        setActions((prevActions) => {
            const prevPermissionActions = prevActions[moderationId] ?? {}

            return {
                ...prevActions,
                [moderationId]: {
                    ...prevPermissionActions,
                    update: {
                        permissionName: moderationPermission.name,
                        actionId: updateAction.id,
                        active: isChecked,
                    },
                },
            }
        })
    }

    const renderTabs = () => {
        const permissions = p_.permissions.filter((permission: Permission) =>
            AVAILABLE_PERMISSION_SECTIONS.includes(permission.name)
        )

        permissions.sort(
            (a: any, b: any) =>
                AVAILABLE_PERMISSION_SECTIONS.indexOf(a.name) - AVAILABLE_PERMISSION_SECTIONS.indexOf(b.name)
        )

        const notificationSubPermissions = permissions.filter((permission: Permission) =>
            NOTIFICATION_PAGE_TABS.includes(permission.name)
        )

        const moderationPermission = permissions.find((permission: Permission) => permission.name === 'moderation')

        return permissions
            .filter(
                (permission: Permission) =>
                    !NOTIFICATION_PAGE_TABS.includes(permission.name) && permission.name !== 'moderation'
            )
            .map((permission: Permission) => (
                <div key={permission.id} tabid={`${permission.id}`} label={translate(`${permission.name}_permissions`)}>
                    <CreateRoleSettings actions={actions} permission={permission} onChange={handleChangeActions} />
                    <ModerationRoleSettings
                        handleModerationPermissionsChange={handleModerationPermissionsChange}
                        permission={permission}
                        moderationPermission={moderationPermission}
                        actions={actions}
                    />
                    {renderList(permission)}
                    {renderSubTabs(permission, notificationSubPermissions)}
                </div>
            ))
    }

    const renderSubTabs = (permission: Permission, subPermissions: Permission[]) => {
        if (permission.name === 'notificationPage') {
            //@ts-ignore
            const isActive = actions[permission.id]['read'].active
            return subPermissions.map((subPermission: Permission) => (
                <div
                    className={cn({
                        [item.disableWrapper]: !isActive,
                    })}
                    key={permission.id}
                    tabid={`${permission.id}`}
                    label={translate(`${permission.name}_permissions`)}
                >
                    <div className={styles.createRoleSettingsHeader}>{translate(subPermission.name)}</div>
                    <CreateRoleSettings actions={actions} permission={subPermission} onChange={handleChangeActions} />
                </div>
            ))
        }
    }

    const handleChangePermissions = (payload: Payload, id: number) => {
        setActions((prevActions: any) => {
            let nextActions = { ...prevActions, [id]: { ...prevActions[id], ...payload } }
            return nextActions
        })
    }

    const onCheckIsDataChanged = () => {
        const omited = omitEmpty(actions)
        return !deepEqual(omited, initialActions) || (editedRole && editedRole.label !== label)
    }

    const handleSave = () => {
        if (!onCheckIsDataChanged()) {
            p_.emitError(editedRole ? 'updateRoleNothingToUpdate' : 'createRoleNothingToSave')
            return
        }

        let updatedPermissonsArray = getPermissionsArray()

        const data: any = {
            label,
            permissions: updatedPermissonsArray,
        }

        if (editedRole) {
            data.id = Number(editedRole.id)
            api.send('updateRole', data).then(() => {
                changeLocation(`/${routes.roles.path}`)
            })
        } else {
            api.send('createRole', data).then(() => {
                changeLocation(`/${routes.roles.path}`)
            })
        }
    }

    const renderList = (permission: Permission) => {
        if (!AVAILABLE_CATALOGS.includes(permission.name) && !AVAILABLE_OLD_CATALOGS.includes(permission.name)) {
            return null
        }

        return AVAILABLE_CATALOGS.includes(permission.name) ? (
            <TreeViewCatalog
                type={CATALOG_TYPES[permission.name]}
                scrollParent={document.getElementById('createRoleTabContentWrapper')}
                permissions={actions[permission.id]}
                onChangePermissions={(payload: Payload) => handleChangePermissions(payload, permission.id)}
            />
        ) : (
            <div className={styles.catalogWrapper}>
                <Catalog
                    key={'advertisingAds'}
                    type={'advertisingCampaigns'}
                    roleMode={true}
                    selectedItems={[]}
                    //onSelect={(devices: any) => {}}
                    hideView={true}
                    hideSelection={true}
                    disposition={'list'}
                    treeView={false}
                    permissions={actions[permission.id]}
                    onChangePermissions={(payload: Payload) => handleChangePermissions(payload, permission.id)}
                />
            </div>
        )
    }

    return (
        <Content
            title={editedRole ? translate('roleEditing') : translate('roleCreation')}
            contentClassName={styles.createRoleContentWrapper}
        >
            <div className={styles.createRoleWrapper}>
                <CreateRoleHeader
                    label={label}
                    onChange={handleChangeLabel}
                    onSave={handleSave}
                    editedRole={editedRole}
                />
                <Tabs
                    type="vertical"
                    className={styles.createRoleTabsWrapper}
                    tabClassName={styles.createRoleTab}
                    activeTabClassName={styles.createRoleTabActive}
                    tabsWrapperClassName={styles.createRoleTabWrapper}
                    contentClassName={styles.createRoleContent}
                    contentId="createRoleTabContentWrapper"
                    onChange={() => {}}
                >
                    {renderTabs()}
                </Tabs>
            </div>
        </Content>
    )
}

const CreateRoleWrapper = (p_: any) => <CreateRole {...p_} />
const mapStateToProps = (state: any) => ({
    permissions: state.user.data.permissions,
})

const mapDispatchToProps = (dispatch: any) => ({
    emitError: (error: any) => dispatch(emitError(error)),
})

const exportCreateRole = checkAvailableModule('users/roles/create')
    ? connect(mapStateToProps, mapDispatchToProps)(CreateRoleWrapper)
    : null

export default exportCreateRole
